import { useCallback, useEffect, useState } from "react"
import { toast } from "sonner"
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  ApiRequestError,
  createBidMemoryItem,
  deleteBidMemoryItem,
  listBidMemory,
  toastApiError,
  updateBidMemoryItem,
  type BidMemoryItem,
} from "@/lib/api"
import { relativeTime } from "@/lib/datetime"
import { getUserDisplayName } from "@/lib/user-display"
import { useAuthStore } from "@/stores/auth-store"

/** Matches the server's CHECK (1–1,000 characters after trimming). */
const MAX_MEMORY_CHARS = 1000

export const BID_MEMORY_EMPTY_COPY =
  "Nothing saved yet. Tell the AI anything it should remember about this bid, and it will save it here."

interface BidMemoryListProps {
  bidId: string
  /** Viewers and non-members see the list without add, edit or delete. */
  readOnly?: boolean
}

/**
 * Bid memory: what people told the AI that will be useful on this bid later.
 * Every AI door on the bid reads these items; the AI adds to them ("Saved to
 * memory") and people can add, edit and remove them here.
 *
 * Freshness: the list loads when it mounts. The AI doors that save items
 * (editor panel, Tender Chat, Assistant) are never on screen together with
 * the Strategy tab, so coming back here always refetches.
 */
export function BidMemoryList({ bidId, readOnly = false }: BidMemoryListProps) {
  const currentUserId = useAuthStore((s) => s.user?.id)

  const [items, setItems] = useState<BidMemoryItem[] | null>(null)
  const [hasLoadError, setHasLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const [isAdding, setIsAdding] = useState(false)
  const [addDraft, setAddDraft] = useState("")
  const [isSavingNew, setIsSavingNew] = useState(false)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState("")
  const [isSavingEdit, setIsSavingEdit] = useState(false)

  const [deletingIds, setDeletingIds] = useState<ReadonlySet<string>>(new Set())

  useEffect(() => {
    let isCancelled = false
    setItems(null)
    setHasLoadError(false)
    listBidMemory(bidId)
      .then(({ items: loaded }) => {
        if (!isCancelled) setItems(loaded)
      })
      .catch(() => {
        if (!isCancelled) setHasLoadError(true)
      })
    return () => {
      isCancelled = true
    }
  }, [bidId, reloadKey])

  const removeRow = useCallback((memoryId: string) => {
    setItems((current) => current?.filter((item) => item.id !== memoryId) ?? current)
  }, [])

  const handleStartAdd = () => {
    setEditingId(null)
    setAddDraft("")
    setIsAdding(true)
  }

  const handleCancelAdd = () => {
    setIsAdding(false)
    setAddDraft("")
  }

  const handleSaveNew = async () => {
    const content = addDraft.trim()
    if (!content || isSavingNew) return
    setIsSavingNew(true)
    try {
      const { item } = await createBidMemoryItem(bidId, content)
      setItems((current) => [...(current ?? []), item])
      setIsAdding(false)
      setAddDraft("")
    } catch (error) {
      toastApiError(error, "Couldn’t save to memory")
    } finally {
      setIsSavingNew(false)
    }
  }

  const handleStartEdit = (item: BidMemoryItem) => {
    setIsAdding(false)
    setEditingId(item.id)
    setEditDraft(item.content)
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditDraft("")
  }

  const handleSaveEdit = async (memoryId: string) => {
    const content = editDraft.trim()
    if (!content || isSavingEdit) return
    setIsSavingEdit(true)
    try {
      const { item } = await updateBidMemoryItem(bidId, memoryId, content)
      setItems((current) => current?.map((existing) => (existing.id === memoryId ? item : existing)) ?? current)
      setEditingId(null)
      setEditDraft("")
    } catch (error) {
      if (error instanceof ApiRequestError && error.statusCode === 404) {
        // Someone removed it while this person was editing.
        removeRow(memoryId)
        setEditingId(null)
        setEditDraft("")
        toast("That item was already removed from memory.")
      } else {
        toastApiError(error, "Couldn’t save your edit")
      }
    } finally {
      setIsSavingEdit(false)
    }
  }

  const handleDelete = async (memoryId: string) => {
    if (deletingIds.has(memoryId)) return
    setDeletingIds((current) => new Set(current).add(memoryId))
    try {
      // 'deleted', 'already_deleted' and 'not_found' all mean the item is gone.
      await deleteBidMemoryItem(bidId, memoryId)
      removeRow(memoryId)
      if (editingId === memoryId) handleCancelEdit()
    } catch (error) {
      toastApiError(error, "Couldn’t remove it from memory")
    } finally {
      setDeletingIds((current) => {
        const next = new Set(current)
        next.delete(memoryId)
        return next
      })
    }
  }

  return (
    <section aria-labelledby="bid-memory-heading" className="min-w-0 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 id="bid-memory-heading" className="text-sm font-medium">
          Memory
        </h3>
        {!readOnly && items !== null && !isAdding && (
          <Button variant="ghost" size="sm" onClick={handleStartAdd}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add
          </Button>
        )}
      </div>

      {isAdding && (
        <div className="space-y-2">
          <Textarea
            value={addDraft}
            onChange={(e) => setAddDraft(e.target.value)}
            maxLength={MAX_MEMORY_CHARS}
            rows={3}
            placeholder="Something the AI should remember about this bid"
            aria-label="New memory item"
            className="text-sm bg-muted"
            autoFocus
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void handleSaveNew()} disabled={!addDraft.trim() || isSavingNew}>
              {isSavingNew && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancelAdd} disabled={isSavingNew}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {items === null && !hasLoadError && (
        <div className="flex items-center gap-2 py-2 text-meta" role="status">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          Loading memory…
        </div>
      )}

      {hasLoadError && (
        <p className="text-meta flex items-center gap-1" role="status">
          <span>Couldn&rsquo;t load memory.</span>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-xs"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Try again
          </Button>
        </p>
      )}

      {items !== null && items.length === 0 && !isAdding && (
        <p className="text-meta py-1">{BID_MEMORY_EMPTY_COPY}</p>
      )}

      {items !== null && items.length > 0 && (
        <ul className="divide-y divide-border" aria-label="Memory items">
          {items.map((item) => {
            const isEditing = editingId === item.id
            const isDeleting = deletingIds.has(item.id)
            const author = getUserDisplayName(
              { userId: item.createdBy, fullName: item.createdByName, email: item.createdByEmail },
              currentUserId
            )
            return (
              <li key={item.id} className="group py-2.5">
                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      maxLength={MAX_MEMORY_CHARS}
                      rows={3}
                      aria-label="Edit memory item"
                      className="text-sm bg-muted"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => void handleSaveEdit(item.id)}
                        disabled={!editDraft.trim() || isSavingEdit}
                      >
                        {isSavingEdit && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={handleCancelEdit} disabled={isSavingEdit}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground whitespace-pre-wrap break-words">{item.content}</p>
                      <p className="text-meta mt-0.5">
                        {author} · {relativeTime(item.createdAt)}
                      </p>
                    </div>
                    {!readOnly && (
                      <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-subtle"
                          aria-label="Edit memory item"
                          onClick={() => handleStartEdit(item)}
                          disabled={isDeleting}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-subtle hover:text-destructive"
                          aria-label="Delete memory item"
                          onClick={() => void handleDelete(item.id)}
                          disabled={isDeleting}
                        >
                          {isDeleting
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
